<template>
  <div class="form">
    <div class="form__content">
      <IdentityQueueStepCounter />
      <h1 class="extra-large-headline">
        {{ $t('InvestorDeclarationForm.headline.header') }}
      </h1>
      <p class="bodycopy">
        {{ $t('InvestorDeclarationForm.body.body') }}
      </p>

      <div class="investor-category">
        <section>
          <div class="investor-category-buttons">
            <MultipleWayToggle
              :options="[
                $t('InvestorDeclarationForm.investorCategory.restricted'),
                $t('InvestorDeclarationForm.investorCategory.highNetWorth'),
              ]"
              @update:toggleValue="
                (updateVal) => updateInvestorCategory(updateVal)
              "
            />
          </div>
        </section>
        <section>
          <!-- :clearResponses="" -->
          <component
            :is="loadDynamicFormPath"
            @update:responses="(updateVal) => updateResponses(updateVal)"
            @update:prompt="(promptStatus) => updatePromptStatus(promptStatus)"
          />
        </section>
        <section>
          <Checkbox
            name="tnc"
            :text="
              $t('Checkbox.text.investorDeclarationTnc', {
                boldText: `<b>${$t('Checkbox.text.investorDeclarationTncBold', {
                  investorCategory: getInvestorCategoryDisplayName(),
                })}</b>`,
              })
            "
            @update:tnc="
              (updateVal) => (investorDeclarationResponse.agreedTo = updateVal)
            "
          />
          <ButtonBase
            :isDisabled="globalStore.getIsFormLoading || !isReadyToSubmit || isPromptShown"
            :isLoading="globalStore.getIsFormLoading"
            @onButtonClick="handleFormSubmit"
          >
            {{ $t('ButtonBase.label.continue') }}
          </ButtonBase>
        </section>
        <IdentityQueueNext />
      </div>
    </div>
  </div>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import {
 ref, computed, defineAsyncComponent, reactive,
} from 'vue';
import { useI18n } from 'vue-i18n';

/* eslint-disable import/no-cycle */
import { useGlobalStore } from '@/stores/globalStore';

import IdentityQueueStepCounter from '@/components/layout/progress/IdentityQueueStepCounter.vue';
import IdentityQueueNext from '@/components/layout/progress/IdentityQueueNext.vue';
import MultipleWayToggle from '@/components/ui/toggles/MultipleWayToggle.vue';
import Checkbox from '@/components/ui/inputs/Checkbox.vue';
import ButtonBase from '@/components/ui/buttons/ButtonBase.vue';

import { capitaliseFirstChar } from '@/composables/helpers/useStringHelpers';
import { useIdentityQueueStore } from '@/stores/identityQueueStore';
import useApiHelpers from '@/composables/api/helpers';

const { t } = useI18n();
const identityQueueStore = useIdentityQueueStore();
const { postRequest } = useApiHelpers();
const route = useRoute();
const router = useRouter();

const investorType = ref('restricted');
const investorDeclarationResponse = reactive({
  categorisation: 'RESTRICTED',
  responses: [],
  agreedTo: false,
});
const isFormCompleted = ref(false);
const isPromptShown = ref(false);

const globalStore = useGlobalStore();
const investorCategoryMapping = ['restricted', 'highNetWorth'];

const loadDynamicFormPath = computed(() => {
  const investor = capitaliseFirstChar(investorType.value.toLowerCase());
  if (!investor) {
    return null;
  }
  return defineAsyncComponent(() => import(/* @vite-ignore */ `./${investor}InvestorForm.vue`));
});

const isReadyToSubmit = computed(
  () => isFormCompleted.value
    && investorDeclarationResponse.agreedTo
    && isPromptShown.value === false,
);

const getInvestorCategoryDisplayName = () => t(
    `InvestorDeclarationForm.investorCategory.${investorType.value}`,
  ).toLowerCase();

const updateResponses = (formResponses) => {
  const { response, isCompleted } = formResponses;
  investorDeclarationResponse.responses = response.filter(
    (item) => item !== undefined,
  );
  isFormCompleted.value = isCompleted;
};

const updatePromptStatus = (promptStatus) => {
  isPromptShown.value = promptStatus;
};

const updateInvestorCategory = (updateVal) => {
  investorType.value = investorCategoryMapping[updateVal];
  investorDeclarationResponse.categorisation = investorType.value.toUpperCase();
  updateResponses({ response: [], isCompleted: false }); // resets the form
};

const handleFormSubmit = () => {
  globalStore.setIsFormLoading(true);

  // TODO: Update this to the new endpoint or create a new api composable
  postRequest(
    '/customers/categorisation',
    investorDeclarationResponse,
    async () => {
      identityQueueStore.updateToNextRoute();
      identityQueueStore.fetchNextIdentityRoute(route).then((nextRoute) => {
        router.push(nextRoute);
      });
      globalStore.setIsFormLoading(false);
    },
  );
};
</script>
<style lang="scss" scoped>
@import '../dofpCommonStyles.scss';
</style>
